/**
 * API Client Service for Smart Storage Checker
 * Provides centralized HTTP client for backend communication
 */

import axios from 'axios';

// Use relative URL (empty string) in production, localhost in development
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

class ApiClient {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 300000, // 5 minutes for large file uploads
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      },
    });

    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        console.log(`API Response: ${response.status} ${response.config.url}`);
        return response;
      },
      (error) => {
        console.error('API Error:', {
          status: error.response?.status,
          url: error.config?.url,
          message: error.response?.data?.message || error.message,
        });
        return Promise.reject(this.handleError(error));
      }
    );
  }

  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      return {
        type: 'API_ERROR',
        status,
        message: data?.message || `HTTP ${status} Error`,
        details: data?.details || null,
        originalError: error,
      };
    } else if (error.request) {
      // Network error
      return {
        type: 'NETWORK_ERROR',
        message: 'Network connection failed',
        details: 'Please check your internet connection',
        originalError: error,
      };
    } else {
      // Other error
      return {
        type: 'UNKNOWN_ERROR',
        message: error.message || 'An unexpected error occurred',
        originalError: error,
      };
    }
  }

  // Health Check
  async healthCheck() {
    const response = await this.client.get('/api/health');
    return response.data;
  }

  // File Upload (single file)
  async uploadFile(file, onProgress = null, powerUnit = 'watt') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('power_unit', powerUnit); // Meeting 2025-10-03

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      };
    }

    const response = await this.client.post('/api/upload', formData, config);
    return response.data;
  }

  // Multiple File Upload with merge
  async uploadMultipleFiles(files, onProgress = null, powerUnit = 'watt') {
    const formData = new FormData();

    // Append all files with the same field name 'files'
    files.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('power_unit', powerUnit); // Meeting 2025-10-03

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      };
    }

    const response = await this.client.post('/api/upload/multi', formData, config);
    return response.data;
  }

  // Start Analysis
  async startAnalysis(uploadId) {
    const response = await this.client.post(`/api/analyze/${uploadId}`);
    return response.data;
  }

  // Get Analysis Results
  async getAnalysisResults(uploadId) {
    const response = await this.client.get(`/api/results/${uploadId}`);
    return response.data;
  }

  // Get Analysis Summary with Supercap Design Parameters
  async getSummary(uploadId) {
    const response = await this.client.get(`/api/reports/summary/${uploadId}`);
    return response.data;
  }

  // Get Chart Data
  async getChartData(uploadId, chartType = 'all') {
    const response = await this.client.get(`/api/charts/${uploadId}`, {
      params: { type: chartType },
    });
    return response.data;
  }

  // Submit Lead/Contact Information
  async submitLead(leadData) {
    const response = await this.client.post('/api/leads', leadData);
    return response.data;
  }

  // Get Report
  async getReport(uploadId, format = 'pdf') {
    const endpoint = format === 'pdf'
      ? `/api/export/${uploadId}/pdf`
      : `/api/export/${uploadId}/json`;

    const response = await this.client.get(endpoint, {
      responseType: format === 'pdf' ? 'blob' : 'json',
    });
    return response.data;
  }

  // Advanced Metrics Endpoints (Meeting 2025-10-03)
  async getFFTSpectrum(uploadId, samplingRate = null) {
    const params = samplingRate ? { sampling_rate: samplingRate } : {};
    const response = await this.client.get(`/api/advanced/fft/${uploadId}`, { params });
    return response.data;
  }

  async getPowerHistogram(uploadId, bins = 50) {
    const response = await this.client.get(`/api/advanced/histogram/${uploadId}`, {
      params: { bins }
    });
    return response.data;
  }

  async getPeakEnergy(uploadId, thresholdMultiplier = 1.5) {
    const response = await this.client.get(`/api/advanced/peak-energy/${uploadId}`, {
      params: { threshold_multiplier: thresholdMultiplier }
    });
    return response.data;
  }

  async getChargingDischarging(uploadId, zeroThreshold = 10.0) {
    const response = await this.client.get(`/api/advanced/charging-discharging/${uploadId}`, {
      params: { zero_threshold: zeroThreshold }
    });
    return response.data;
  }

  async getMovingAverage(uploadId, windowSize = 10) {
    const response = await this.client.get(`/api/advanced/moving-average/${uploadId}`, {
      params: { window_size: windowSize }
    });
    return response.data;
  }

  async getComprehensiveAnalysis(uploadId, options = {}) {
    const params = {
      include_fft: options.includeFFT ?? true,
      include_peak: options.includePeak ?? true,
      include_charging: options.includeCharging ?? true,
      sampling_rate: options.samplingRate ?? null
    };
    const response = await this.client.get(`/api/advanced/comprehensive/${uploadId}`, { params });
    return response.data;
  }

  // Generic GET request
  async get(endpoint, params = {}) {
    const response = await this.client.get(endpoint, { params });
    return response.data;
  }

  // Generic POST request
  async post(endpoint, data = {}) {
    const response = await this.client.post(endpoint, data);
    return response.data;
  }

  // Generic PUT request
  async put(endpoint, data = {}) {
    const response = await this.client.put(endpoint, data);
    return response.data;
  }

  // Generic DELETE request
  async delete(endpoint) {
    const response = await this.client.delete(endpoint);
    return response.data;
  }
}

// Create singleton instance
const apiClient = new ApiClient();

// Named exports for specific operations
export const uploadFile = (file, onProgress, powerUnit) => apiClient.uploadFile(file, onProgress, powerUnit);
export const uploadMultipleFiles = (files, onProgress, powerUnit) => apiClient.uploadMultipleFiles(files, onProgress, powerUnit);
export const startAnalysis = (uploadId) => apiClient.startAnalysis(uploadId);
export const getAnalysisResults = (uploadId) => apiClient.getAnalysisResults(uploadId);
export const getSummary = (uploadId) => apiClient.getSummary(uploadId);
export const getChartData = (uploadId, chartType) => apiClient.getChartData(uploadId, chartType);
export const submitLead = (leadData) => apiClient.submitLead(leadData);
export const getReport = (uploadId, format) => apiClient.getReport(uploadId, format);
export const healthCheck = () => apiClient.healthCheck();

// Advanced metrics exports (Meeting 2025-10-03)
export const getFFTSpectrum = (uploadId, samplingRate) => apiClient.getFFTSpectrum(uploadId, samplingRate);
export const getPowerHistogram = (uploadId, bins) => apiClient.getPowerHistogram(uploadId, bins);
export const getPeakEnergy = (uploadId, threshold) => apiClient.getPeakEnergy(uploadId, threshold);
export const getChargingDischarging = (uploadId, zeroThreshold) => apiClient.getChargingDischarging(uploadId, zeroThreshold);
export const getMovingAverage = (uploadId, windowSize) => apiClient.getMovingAverage(uploadId, windowSize);
export const getComprehensiveAnalysis = (uploadId, options) => apiClient.getComprehensiveAnalysis(uploadId, options);

// Default export
export default apiClient;